import $ from 'jquery';

// @types/select2 を使いたいが @types/jquery に依存していて、 @types/jquery が jquery 3.x 用なので
// jquery をアップデートするまでは必要な部分を抜き出して使う。
// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/select2/index.d.ts

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Select2 {
  export interface QueryOptions {
    term?: string | undefined;
    page?: number | undefined;
  }
  export interface TranslationArg {
    input: string;
    minimum: number;
    maximum: number;
  }
  export interface OptionData {
    disabled: boolean;
    element: HTMLOptionElement;
    id: string;
    selected: boolean;
    text: string;
    title: string;

    loading?: undefined;
    children?: undefined;
  }
}

type CompanyQueryParam = {
  keyword: string;
  with_unpublished?: boolean;
};

type videoBundleCompanyQueryParam = {
  company_ids: string;
  keyword: string;
};

type CompanyQueryResult = {
  companies: { id: number; name: string }[];
};

type VideoChaptersResult = {
  video_chapters: { id: number; title: string }[];
};

type UniversityQueryResult = {
  universities: { id: number; name: string }[];
  truncated: boolean;
};

const select2 = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  const maxSelection = $elem.data('maxSelection');
  const options = {
    width: '100%',
    language: {
      noResults: () => '候補が見つかりません',
      maximumSelected: (args: Select2.TranslationArg) => `${args.maximum}件しか選択できません`,
    },
    maximumSelectionLength: maxSelection === null || maxSelection === undefined ? undefined : maxSelection,
  };
  $elem.select2(options);
};
const select2Company = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  const withUnpublished = $elem.data('withUnpublished');
  $elem.select2({
    debug: true,
    minimumInputLength: 1,
    width: '100%',
    language: {
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      inputTooShort: (args: Select2.TranslationArg) => `${args.minimum}文字以上入力してください`,
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        const opts: CompanyQueryParam = {
          keyword: params.term!, // ここで params.term が undefined になることはなさそう
        };
        if (withUnpublished) {
          opts.with_unpublished = true;
        }
        return opts;
      },
      processResults(data: CompanyQueryResult, _params: Select2.QueryOptions) {
        return { results: data.companies };
      },
    },
  });
};

// 在学中の学校（大学・短大・高専・専門学校）の候補は 3,000 件以上あり、<option> として全件描画すると
// ページが重くなる。初期状態では選択済みの1件だけを描画し、入力されてはじめてサーバから候補を取得する。
// NOTE: クラス名に js-search-select-university を使ってはいけない。company_statistic_contents.coffee が
// そのクラスに対して select2:select で OC.select2（ajaxなし）を張り直すため、1回選ぶとローカル絞り込みに化ける
const select2School = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    width: '100%',
    minimumInputLength: 1,
    language: {
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      inputTooShort: () => '学校名を入力してください',
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        return { keyword: params.term == null ? '' : params.term };
      },
      processResults(data: UniversityQueryResult) {
        const results: { id: number | string; text: string; disabled?: boolean }[] = data.universities.map(
          (university) => ({ id: university.id, text: university.name }),
        );
        if (data.truncated) {
          // 上限を超えた分は返していないので、選べない行で絞り込みを促す
          results.push({ id: '', text: '他にも候補があります。学校名をさらに入力してください', disabled: true });
        }
        return { results };
      },
    },
  });
};

const select2PromotionContract = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    debug: true,
    width: '100%',
    language: {
      noResults: () => '候補が見つかりません'
    },
  });
};

const select2VideoChapter = (elem: HTMLElement | string) => {
  const $elem = $(elem);
  $elem.select2({
    debug: true,
    width: '100%',
    language: {
      noResults: () => '候補が見つかりません',
      searching: () => '検索中...',
      errorLoading: () => '読み込みに失敗しました。入力し直してください。',
    },
    ajax: {
      delay: 250,
      url: $elem.data('url'),
      data(params: Select2.QueryOptions) {
        const opts: videoBundleCompanyQueryParam = {
          company_ids: $elem.attr('data-company-ids'),
          keyword: params.term!,
        };
        return opts;
      },
      processResults(data: VideoChaptersResult) {
        return { results: data.video_chapters };
      },
    },
  });
};

window.OC = window.OC || {};
window.OC.select2 = select2;

$(document).on('nested:fieldAdded', (e: any) => {
  e.field.find('.js-search-select').each((_: number, elem: HTMLElement) => select2(elem));
  e.field.find('.js-search-select-company').each((_: number, elem: HTMLElement) => select2Company(elem));
  e.field.find('.js-search-select-school').each((_: number, elem: HTMLElement) => select2School(elem));
  e.field.find('.js-search-select-promotion-contract').each((_: number, elem: HTMLElement) => select2PromotionContract(elem));
  e.field.find('.js-search-select-video-chapter').each((_: number, elem: HTMLElement) => select2VideoChapter(elem));
});

window.addEventListener('DOMContentLoaded', () => {
  select2('.js-search-select');
  select2Company('.js-search-select-company');
  select2School('.js-search-select-school');
  select2PromotionContract('.js-search-select-promotion-contract');
  select2VideoChapter('.js-search-select-video-chapter');
});
